import { LocalStorage, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import { SessionStorageValue, getSessionStorage, setSessionStorage } from '@flipster/utils/sessionStorage';
import { type Nullable } from '@flipster/utils/typescript';

export type MaybePromise<T> = T | Promise<T>;

export interface PersistStateDelegate<T> {
  getItem: <K extends keyof T>(key: K) => MaybePromise<Nullable<T[K]>>;
  setItem: <K extends keyof T>(key: K, value: T[K]) => void;
  subscribe: <K extends keyof T>(key: K, callback: (value: T[K]) => void, defaultValue: T[K]) => void;
}

export const PersistStateLocalStorageDelegate: PersistStateDelegate<LocalStorage> = {
  getItem: getLocalStorage,
  setItem: setLocalStorage,
  subscribe: (key, callback) => {
    const storageEventCallback = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === key) {
        const newValue = (() => {
          const value = e.newValue;
          try {
            if (!value) return null;
            return JSON.parse(value);
          } catch {
            return value ?? null;
          }
        })();
        callback(newValue as never);
      }
    };
    window.addEventListener('storage', storageEventCallback);
    return () => {
      window.removeEventListener('storage', storageEventCallback);
    };
  },
};

export const PersistStateSessionStorageDelegate: PersistStateDelegate<SessionStorageValue> = {
  getItem: getSessionStorage,
  setItem: setSessionStorage,
  subscribe: (key, callback) => {
    const storageEventCallback = (e: StorageEvent) => {
      if (e.storageArea === sessionStorage && e.key === key) {
        const newValue = (() => {
          const value = e.newValue;
          try {
            if (!value) return null;
            return JSON.parse(value);
          } catch {
            return value ?? null;
          }
        })();
        callback(newValue as never);
      }
    };
    window.addEventListener('storage', storageEventCallback);
    return () => {
      window.removeEventListener('storage', storageEventCallback);
    };
  },
};
